import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Info } from 'lucide-react';
import { debounce } from 'lodash';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Separator } from '../../../components/ui/separator';
import {
  SettingsSection,
  SettingsLayout,
} from '../../../components/ui/settings-section';
import GoogleMeetIntegration from './google/GoogleMeet';
import ContactSalesDialog from '../../sales/ContactSalesDialog';
import MicrosoftTeamsCalendarIntegration from './microsoft/MicrosoftTeams';
import ZoomMeetingsIntegration from './zoom/ZoomMeetings';
import { config } from '../../../../config';
import BotHistory from './history/BotHistory';
import { usePlan } from '../../plan/hooks/plan';
import { RootState } from '../../../reducers';
import { updateUserProfile } from '../settingsSlice';

const MeetingBotSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isPlan } = usePlan();
  const [salesDialogOpen, setSalesDialogOpen] = React.useState(false);
  const { user, updateStatus } = useSelector((state: RootState) => ({
    user: state?.settings.user,
    updateStatus: state?.settings.updateSettings.status,
  }));
  const [hasUpdated, setHasUpdated] = useState(false);

  const defaultMeetingBotName = `${user.name}'s Notetaker`;
  const [meetingBotName, setMeetingBotName] = useState<string>(
    user.integrations?.meetingBotName || defaultMeetingBotName,
  );

  useEffect(() => {
    setMeetingBotName(
      user.integrations?.meetingBotName || `${user.name}'s Notetaker`,
    );
  }, [user.integrations?.meetingBotName, user.name]);

  const debouncedMeetingBotNameUpdate = useCallback(
    debounce((value: string) => {
      if (value && value !== user.integrations?.meetingBotName) {
        setHasUpdated(true);
        dispatch(
          updateUserProfile({
            name: user.name,
            integrations: { meetingBotName: value },
          }),
        );
      }
    }, 1000),
    [user.integrations?.meetingBotName, user.name],
  );

  const handleMeetingBotNameChange = (value: string) => {
    setMeetingBotName(value);
    debouncedMeetingBotNameUpdate(value);
  };

  // Show toast notification when update succeeds
  useEffect(() => {
    if (updateStatus === 'succeeded' && hasUpdated) {
      toast(t('profile.updated', 'Profile updated successfully'), {
        duration: 2000,
      });
      setHasUpdated(false); // Reset after showing toast
    }
  }, [updateStatus, hasUpdated, t]);

  const handleSalesDialogClose = () => {
    setSalesDialogOpen(false);
  };

  const hasActiveIntegration =
    user.integrations?.google?.calendar?.isActive ||
    user.integrations?.microsoft?.calendar?.isActive ||
    user.integrations?.zoomPlatform?.generalApp?.isActive;

  return (
    <SettingsLayout>
      {/* Notetaker Name Setting */}
      <SettingsSection
        label={t(
          'settings.meetingBot.meetingBotName.label',
          'Meeting Bot Name',
        )}
        description={t(
          'settings.meetingBot.meetingBotName.description',
          'Customize the name that appears when your bot joins meetings',
        )}
        showSeparator={false}
      >
        <Input
          id="meetingBotName"
          value={meetingBotName}
          onChange={(e) => handleMeetingBotNameChange(e.target.value)}
          placeholder={defaultMeetingBotName}
          className="max-w-[300px]"
        />
      </SettingsSection>

      <Separator />

      {hasActiveIntegration && (
        <>
          <div className="flex items-center gap-2 px-4 py-3 bg-muted/30 rounded-md">
            <Info className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
            <p className="text-xs text-muted-foreground leading-none">
              {t(
                'settings.meetingBot.instructions',
                `When your meeting starts, admit "${meetingBotName}" to begin recording`,
              )}
            </p>
          </div>
          <Separator />
        </>
      )}

      {!hasActiveIntegration && <Separator />}
      {/* Google Meet Bot */}
      {user.integrations?.google?.calendar?.isActive ? (
        <GoogleMeetIntegration />
      ) : (
        <SettingsSection
          label={t('settings.meetingBot.googleMeet.label', 'Google Calendar')}
          description={t(
            'settings.meetingBot.googleMeet.description',
            'Auto-record meetings',
          )}
          showSeparator={false}
        >
          <GoogleMeetIntegration />
        </SettingsSection>
      )}

      <Separator />

      {/* Microsoft Teams Bot */}
      {user.integrations?.microsoft?.calendar?.isActive ? (
        <MicrosoftTeamsCalendarIntegration />
      ) : (
        <SettingsSection
          label={t(
            'settings.meetingBot.microsoftTeams.label',
            'Microsoft Calendar',
          )}
          description={t(
            'settings.meetingBot.microsoftTeams.description',
            'Auto-record meetings',
          )}
          showSeparator={false}
        >
          <MicrosoftTeamsCalendarIntegration />
        </SettingsSection>
      )}

      <Separator />

      {/* Zoom Bot */}
      {config.enableZoomIntegration && (
        <>
          <SettingsSection
            label={t(
              'settings.meetingBot.zoom.label',
              'Zoom Meetings Recorder (Beta)',
            )}
            description={t(
              'settings.meetingBot.zoom.description',
              'Auto-record Zoom meetings with transcripts and AI summaries',
            )}
            showSeparator={false}
          >
            <ZoomMeetingsIntegration />
          </SettingsSection>
          <Separator />
        </>
      )}

      {/* Meeting Bot Status */}
      <SettingsSection
        label={t(
          'settings.meetingBot.status.label',
          'Meeting Bot Notifications',
        )}
        description={t(
          'settings.meetingBot.status.description',
          'Click View History to check the status of recent meeting bots.',
        )}
        showSeparator={false}
      >
        <BotHistory />
      </SettingsSection>

      {/* Enterprise Features */}
      {!isPlan(['ENTERPRISE']) && (
        <>
          <Separator />
          <SettingsSection
            label={t(
              'settings.meetingBot.enterprise.label',
              'Meeting Bot for Organizations',
            )}
            description={t(
              'settings.meetingBot.enterprise.description',
              'Unlimited recordings, custom AI models, and enterprise security',
            )}
            showSeparator={false}
          >
            <Button
              variant="minimal"
              onClick={() => setSalesDialogOpen(true)}
              className="text-black hover:text-black"
            >
              {t(
                'settings.meetingBot.enterprise.contactSales',
                'Contact Sales',
              )}
            </Button>
          </SettingsSection>
        </>
      )}

      {salesDialogOpen && (
        <ContactSalesDialog
          onClose={handleSalesDialogClose}
          formSource="Meeting Bot Settings - Organization Enquiry"
        />
      )}
    </SettingsLayout>
  );
};

export default MeetingBotSettings;
